import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ExternalLink, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

const gptList = [
  { title: 'Numerology', uses: '50K+', description: 'Generate personalized numerology readings with birth dates and names.', link: 'https://chatgpt.com/g/g-F1uLlcCiS-numerology', emoji: '🔢' },
  { title: 'Lyric Finder', uses: '5K+', description: 'Find lyrics by phrase, theme, or artist—perfect for music fans.', link: 'https://chatgpt.com/g/g-jtvn8XkUX-lyric-finder', emoji: '🎵' },
  { title: 'Comic Maker', uses: '10K+', description: 'Build comic strip ideas, characters, plots, and dialogue prompts.', link: 'https://chatgpt.com/g/g-GWytS5R0u-comic-maker', emoji: '💬' },
  { title: 'Holistic Medicine', uses: '10K+', description: 'Explore natural remedies, herbal medicine, and holistic wellness advice.', link: 'https://chatgpt.com/g/g-8c6xenUnL-holistic-medicine', emoji: '🌿' },
  { title: 'Sports Betting Guide', uses: '10K+', description: 'Smart betting tips, odds breakdowns, and predictive strategies.', link: 'https://chatgpt.com/g/g-aCiDh3gtI-sports-betting-guide', emoji: '🏆' },
  { title: 'I Spy Creator', uses: '5K+', description: "Create your own 'I Spy' games with fun prompts and object lists.", link: 'https://chatgpt.com/g/g-2pq1obvX4-i-spy-creator', emoji: '🔍' },
  { title: 'Apple Shortcuts', uses: '1K+', description: 'Find or create Apple Shortcuts fast, with step-by-step guidance.', link: 'https://chatgpt.com/g/g-efCYTMOD0-apple-shortcuts', emoji: '⌨️' },
  { title: 'Rhyme Master', uses: '1K+', description: 'Generate perfect rhymes for lyrics, poems, and creative writing.', link: 'https://chatgpt.com/g/g-jLPFsRxzn-rhyme-master', emoji: '📝' },
  { title: 'Construction Calculator', uses: '1K+', description: 'Instantly calculate dimensions, materials, and cost estimates.', link: 'https://chatgpt.com/g/g-48VODWKqt-construction-calculator', emoji: '🏗️' },
  { title: 'Melody Maker', uses: '1K+', description: 'Generate melodies, hooks, and chord progressions for music production.', link: 'https://chatgpt.com/g/g-MR9MqFk3n-melody-maker', emoji: '🎼' },
  { title: 'Scary Stories', uses: '1K+', description: 'Write spooky, original horror stories with prompts and twists.', link: 'https://chatgpt.com/g/g-LyaA8FSiQ-scary-stories', emoji: '👻' },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const Gpts: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>GPTs by Elola Technologies | Custom AI Tools by Marcus S. Elola</title>
        <meta name="description" content="Explore 166+ custom GPTs built by Marcus S. Elola — from Numerology and Lyric Finders to AI calculators, story generators, betting tools, and more." />
        <link rel="canonical" href="https://elola.tech/GPTs" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Elola Technologies" />
        <meta property="og:title" content="GPTs by Elola Technologies | 166+ Custom AI Tools" />
        <meta property="og:description" content="Check out over 166 GPTs created by Marcus S. Elola — including Numerology, Lyric Finder, Rhyme Master, Comic Maker, and more." />
        <meta property="og:image" content="https://elola.tech/preview-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://elola.tech/GPTs" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="GPTs by Elola Technologies | 166+ Custom AI Tools" />
        <meta name="twitter:description" content="Check out over 166 GPTs created by Marcus S. Elola — including Numerology, Lyric Finder, Rhyme Master, Comic Maker, and more." />
        <meta name="twitter:image" content="https://elola.tech/preview-image.png" />
      </Helmet>

      <div style={{ background: 'var(--bg-base)', minHeight: '100vh', paddingTop: 'var(--navbar-height)' }}>
        {/* Page Header */}
        <section style={{
          padding: 'clamp(50px, 7vw, 90px) var(--page-padding-x) clamp(40px, 5vw, 70px)',
          textAlign: 'center',
          background: 'linear-gradient(180deg, var(--bg-surface) 0%, var(--bg-base) 100%)',
          borderBottom: '1px solid var(--border-card)',
        }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="section-tag" style={{ justifyContent: 'center' }}>
              <Sparkles size={13} />
              166+ Custom GPTs
            </div>
            <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 800, marginBottom: '16px' }}>
              Our Trending <span className="gradient-text">GPTs</span>
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'clamp(15px, 1.5vw, 17px)', maxWidth: '520px', margin: '0 auto 28px' }}>
              Explore Marcus S. Elola's growing library of custom AI tools built for the ChatGPT platform.
            </p>
          </motion.div>
        </section>

        {/* Grid */}
        <section style={{ padding: 'clamp(48px, 6vw, 80px) var(--page-padding-x)', maxWidth: 'calc(var(--page-max-width) + 160px)', margin: '0 auto' }}>
          <motion.div
            className="auto-grid-3"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}
          >
            {gptList.map((gpt) => (
              <motion.div
                key={gpt.title}
                variants={itemVariants}
                whileHover={{ y: -4, boxShadow: '0 12px 40px rgba(109,74,255,0.18)' }}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-card)',
                  borderRadius: '14px',
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  transition: 'border-color 0.2s',
                }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--border-subtle)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border-card)')}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '28px', lineHeight: 1 }}>{gpt.emoji}</span>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'var(--accent-gold)',
                    background: 'rgba(245, 158, 11, 0.1)',
                    border: '1px solid rgba(245, 158, 11, 0.2)',
                    padding: '4px 10px',
                    borderRadius: '50px',
                  }}>
                    {gpt.uses} uses
                  </span>
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: 700 }}>{gpt.title}</h3>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.6', flex: 1 }}>
                  {gpt.description}
                </p>
                <a
                  href={gpt.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  style={{ padding: '10px 18px', fontSize: '13px', alignSelf: 'flex-start' }}
                >
                  <ExternalLink size={13} />
                  Try it
                </a>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* CTA */}
        <section style={{
          padding: 'clamp(50px, 6vw, 80px) var(--page-padding-x)',
          textAlign: 'center',
          borderTop: '1px solid var(--border-card)',
        }}>
          <h2 style={{ fontSize: 'var(--text-h2)', fontWeight: 700, marginBottom: '16px' }}>
            Need a Custom AI Tool?
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '28px', maxWidth: '420px', margin: '0 auto 28px' }}>
            We build bespoke GPTs, AI chatbots, and automation tools tailored to your exact needs.
          </p>
          <Link to="/contact" className="btn btn-primary">
            Build My AI Tool
          </Link>
        </section>
      </div>
    </>
  );
};

export default Gpts;
